import { QueryClient, useQuery } from "@tanstack/react-query";

import { API, graphqlOperation } from "aws-amplify";

import { GraphQLQuery } from "@aws-amplify/api";

import {
  listCircleUsers,
  getCGPercentContext,
  circleUsersByCircleUserUserId,
  circleUsersByCircleUserCircleId,
  getListContext,
} from "@/graphql/queries";

import {
  customGetCircle,
  customGetListContext,
  customListCircles,
} from "@/graphql/customQueries/index";

import {
  joinCircle,
  createCircle,
  deleteCircleUser,
  deleteCircle,
  inviteUserToCircle,
  updateCircle,
  updateCircleUser,
  updateMembershipStatus,
  recordVisitByContext,
  addNewCircle,
  deleteCircleEventPost,
} from "@/graphql/mutations";

import {
  IDataCreateCircle,
  IDataUpdateCircle,
  IInviteCircleMutation,
  IDataDeleteCircle,
  IUpdateInvitedCircle,
  IDataLeaveUserCircle,
  IListCirclesQueryVariables,
  IUpdateInvitedCircleMutation,
  ILeaveCircleMutation,
  IDeleteCircleMutation,
  IUpdateCircleMutation,
  IUpdateMembershipStatusMutation,
  IGetCircleQueryVariables,
} from "@/types";
import { sortRecordsByKey } from "@/utils/sortGraphqlRecords";
import useAuthStore from "@/store/useAuthStore";
import useCircleEventStore, { IcircleEventStore } from "./useCircleEventStore";
import { chunkArray } from "./useMyCalendar";
import { Circle } from "@/models";
import { GetListContextQuery } from "@/graphql/graphql";
import useVisitOtherProfileStore, {
  IVisitOtherProfileStore,
} from "./useVisitOtherProfileStore";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: Infinity,
    },
  },
});

const fetchCircles = async (
  limit?: number,
  id?: string,
  setState?: (p: IcircleEventStore) => void
) => {
  const variables: IListCirclesQueryVariables = {
    filter: { _deleted: { ne: true } },
    limit: limit || 1000,
  };

  const response: any = await API.graphql(
    graphqlOperation(customListCircles, variables)
  );
  const records: any = response?.data?.listCircles ?? {};

  const sortedData = sortRecordsByKey(records.items, "name");

  const dataMyCircles = () => {
    return (
      sortedData?.filter((item: any) => {
        return item.members?.items.some((row: any) => {
          return row.circleUserUserId === id && row.status === "APPROVED";
        });
      }) || []
    );
  };

  const dataAllCircles = () => {
    return (
      sortedData?.filter((item: any) => {
        return item.members?.items.some((row: any) => {
          return row.circleUserUserId !== id;
        });
      }) || []
    );
  };

  if (setState) {
    setState({
      allCircles: dataAllCircles() as never,
    });
  }

  return { ...records, items: sortedData };
};

const fetchCGPercentContext = async (params: {
  referenceIds: string[];
  type?: "EVENT" | "CIRCLE";
  limit?: number;
  isHR?: boolean;
}) => {
  const response: any = await API.graphql(
    graphqlOperation(getCGPercentContext, params)
  );
  const records: any[] = response?.data?.getCGPercentContext ?? [];

  return records;
};

const fetchCircle = async (id: string) => {
  const variables: IGetCircleQueryVariables = {
    id: id,
    eventFilter: { _deleted: { ne: true } },
  };
  const response: any = await API.graphql(
    graphqlOperation(customGetCircle, variables)
  );
  const records: any = response?.data?.getCircle ?? {};

  return records;
};

const fetchCircleInvited = async (id: string) => {
  const response: any = await API.graphql(
    graphqlOperation(circleUsersByCircleUserUserId, {
      circleUserUserId: id,
      filter: {
        //   circleUserUserId: { eq: id },
        status: { eq: "PENDING" },
        _deleted: { ne: true },
      },
    })
  );
  const records: any[] =
    response?.data?.circleUsersByCircleUserUserId?.items || [];

  return records;
};

const fetchCircleJoined = async (id: string) => {
  const response: any = await API.graphql(
    graphqlOperation(listCircleUsers, {
      filter: {
        circleUserCircleId: { eq: id },
        status: { eq: "APPROVED" },
        _deleted: { ne: true },
      },
    })
  );
  const records: any[] = response?.data?.listCircleUsers?.items || [];

  return records;
};

const fetchCircleMembers = async (id: string) => {
  const response: any = await API.graphql(
    graphqlOperation(listCircleUsers, {
      filter: {
        circleUserCircleId: { eq: id },
      },
    })
  );
  const records: any[] = response?.data?.listCircleUsers?.items || [];

  return records;
};

const fetchCircleByMember = async (id: string) => {
  const response: any = await API.graphql(
    graphqlOperation(listCircleUsers, {
      filter: {
        circleUserUserId: { eq: id },
      },
    })
  );
  const records: any[] = response?.data?.listCircleUsers?.items || [];

  return records;
};

const fetchCircleUsers = async (id: string) => {
  const response: any = await API.graphql(
    graphqlOperation(customListCircles, {
      filter: {
        creator: { eq: id },
      },
    })
  );
  const records: any[] = response?.data?.listCircles?.items ?? [];

  return records;
};

const userJoinCircle = async (id: string) => {
  return await API.graphql<GraphQLQuery<any>>({
    query: joinCircle,
    variables: { circleId: id },
  });
};

const createMyCircle = async (payload: IDataCreateCircle) => {
  const records = await API.graphql<GraphQLQuery<any>>({
    query: addNewCircle,
    variables: payload,
  });

  return records?.data?.addNewCircle;
};

const deleteMyCircle = async (id: string, version: string) => {
  const payload: IDataDeleteCircle = {
    id: id,
    _version: version,
  };

  const records = await API.graphql<GraphQLQuery<IDeleteCircleMutation>>({
    query: deleteCircle,
    variables: { input: payload },
  });

  return records;
};

const updateMyCircle = async (payload: IDataUpdateCircle) => {
  const records = await API.graphql<GraphQLQuery<IUpdateCircleMutation>>({
    query: updateCircle,
    variables: { input: payload },
  });

  return records;
};

const updateInvitedCircle = async (payload: IUpdateInvitedCircle) => {
  const records = await API.graphql<GraphQLQuery<IUpdateInvitedCircleMutation>>(
    {
      query: updateCircleUser,
      variables: { input: payload },
    }
  );

  return records;
};

const userLeaveCircle = async (userId: string, version: string) => {
  const payload: IDataLeaveUserCircle = {
    id: userId,
    _version: version,
  };
  const records = await API.graphql<GraphQLQuery<ILeaveCircleMutation>>({
    query: deleteCircleUser,
    variables: {
      input: payload,
    },
  });

  return records;
};

const inviteUser = async (
  circleId: string,
  targetUserIds: string[],
  notes?: string,
  isFromHR?: boolean
) => {
  const records = await API.graphql<GraphQLQuery<IInviteCircleMutation>>({
    query: inviteUserToCircle,
    variables: {
      circleId,
      isHR: isFromHR,
      role: "MEMBER",
      targetUserIds,
      notes,
    },
  });

  return records?.data?.inviteUserToCircle;
};

const useUpdateMembershipStatus = async (
  referenceType: "CIRCLE" | "EVENT",
  referenceId: string,
  status: string,
  isHR?: boolean
) => {
  const records = await API.graphql<
    GraphQLQuery<IUpdateMembershipStatusMutation>
  >({
    query: updateMembershipStatus,
    variables: { referenceType, referenceId, status, isHR: isHR || false },
  });

  return records?.data?.updateMembershipStatus;
};

const useListCircles = (limit?: number, id?: string) => {
  const { setState } = useCircleEventStore();

  return useQuery({
    queryKey: ["KEY_LIST_CIRCLES", limit],
    queryFn: () => fetchCircles(limit, id, setState),
  });
};

const useGetCircle = (id: string, enabled?: boolean) => {
  return useQuery({
    queryKey: ["KEY_GET_CIRCLE", id],
    queryFn: () => fetchCircle(id),
    enabled: enabled === undefined ? true : enabled,
  });
};

const useGetCircleInvited = (id: string) => {
  return useQuery({
    queryKey: ["KEY_GET_CIRCLE_INVITED", id],
    queryFn: () => fetchCircleInvited(id),
  });
};

const useGetCircleJoined = (id: string) => {
  return useQuery({
    queryKey: ["KEY_GET_CIRCLE_JOINED", id],
    queryFn: () => fetchCircleJoined(id),
  });
};

const useGetCircleMembers = (id: string) => {
  return useQuery({
    queryKey: ["KEY_GET_CIRCLE_MEMBERS", id],
    queryFn: () => fetchCircleMembers(id),
  });
};

const useGetCircleByMember = (id: string) => {
  return useQuery({
    queryKey: ["KEY_GET_CIRCLE_BY_MEMBER", id],
    queryFn: () => fetchCircleByMember(id),
  });
};

const useListCircleUsers = (id: string) => {
  return useQuery({
    queryKey: ["KEY_LIST_CIRCLE_USERS", id],
    queryFn: () => fetchCircleUsers(id),
  });
};

const useGetCGPercentContext = (params: {
  referenceIds: string[];
  type?: "EVENT" | "CIRCLE";
  limit?: number;
  isHR?: boolean;
}) => {
  return useQuery({
    queryKey: [
      "KEY_GET_CGPERCENT_CONTENT",
      params.referenceIds.toString(),
      params?.type,
      params?.limit,
      params?.isHR,
    ],
    queryFn: () => fetchCGPercentContext(params),
  });
};

const useGetCGPercentContextWait = (
  params: {
    referenceIds: string[];
    type?: "EVENT" | "CIRCLE";
    limit?: number;
  },
  referenceSource?: string
) => {
  return useQuery({
    queryKey: [
      "KEY_GET_CGPERCENT_CONTENT",
      params.referenceIds.join("_"),
      referenceSource,
    ],
    queryFn: () => fetchCGPercentContext(params),
    enabled: false,
  });
};

const prefetchListCircle = async (id: string) => {
  await queryClient.prefetchQuery({
    queryKey: ["PREFETCH_LIST_EVENTS"],
    queryFn: () => fetchCircles(undefined, id),
  });
};

const useGetCircleInviteeUser = (circleId: string) => {
  return useQuery({
    queryKey: ["KEY_GET_USER_CIRCLE_INVITE", circleId],
    queryFn: async () => {
      const response: any = await API.graphql({
        query: circleUsersByCircleUserCircleId,
        variables: {
          circleUserCircleId: circleId,
          filter: {
            status: {
              eq: "APPROVED",
            },
            _deleted: { ne: true },
          },
        },
      });

      return response?.data?.circleUsersByCircleUserCircleId?.items;
    },
  });
};

type setGlobalState = (p: IcircleEventStore) => void;

export const fetchMyCircles = async (id: string, setState: setGlobalState) => {
  const response = (await API.graphql(
    graphqlOperation(getListContext, {
      id,
      context: "CIRCLE",
    })
  )) as any;

  const records = (response?.data?.getListContext?.circles as Circle[]) || [];

  setState({ myCircles: records });
};

export const useGetMyCircle = (id: string) => {
  const { setState } = useCircleEventStore();
  return useQuery({
    queryKey: ["GET_MY_CIRCLE", id],
    queryFn: () => fetchMyCircles(id, setState),
    enabled: false,
  });
};

export const fetchRecommendedCircles = async (
  id: string,
  setState: setGlobalState,
  noLimit?: boolean
) => {
  const variables: {
    id: string;
    context: string;
    limit?: number;
  } = {
    id,
    context: "RECOMMEND_CIRCLE",
    limit: 1000,
  };

  if (noLimit) {
    variables.limit = 0;
    const response = (await API.graphql(
      graphqlOperation(customGetListContext, variables)
    )) as any;

    const records =
      (response?.data?.getListContext?.recommendedCircles as Circle[]) || [];

    return records;
  }

  const response = (await API.graphql(
    graphqlOperation(customGetListContext, variables)
  )) as any;

  const records =
    (response?.data?.getListContext?.recommendedCircles as Circle[]) || [];

  const dataCirclesID = records?.map((item) => item?.id) || [];

  const CGPCircles = await fetchCGPercentContext({
    referenceIds: dataCirclesID,
    type: "EVENT",
    limit: Number(dataCirclesID?.length),
  });
  setState({ allCircles: records, allCirclesCGP: CGPCircles });

  return records;
};

export const useGetRecommendedCircle = (id: string, noLimit?: boolean) => {
  const { setState } = useCircleEventStore();
  return useQuery({
    queryKey: ["GET_RECOMMENDED_CIRCLES"],
    queryFn: () => fetchRecommendedCircles(id, setState, noLimit),
    enabled: false,
  });
};

export const updateCircleUserVisit = async (
  circleId: string,
  userId: string
) => {
  await API.graphql(
    graphqlOperation(recordVisitByContext, {
      contextId: circleId,
      contextType: "CIRCLE",
    })
  );
};

type setGlobalStateVisit = (p: IVisitOtherProfileStore) => void;

export const fetchVisitProfileCircles = async (
  id: string,
  setState: setGlobalStateVisit
) => {
  const response = (await API.graphql(
    graphqlOperation(getListContext, {
      id,
      context: "CIRCLE",
    })
  )) as any;

  const records = (response?.data?.getListContext?.circles as Circle[]) || [];

  setState({ circles: records });
};

export const useGetVisitProfileCircle = (id: string) => {
  const { setState } = useVisitOtherProfileStore();
  return useQuery({
    queryKey: ["GET_VISIT_CIRCLE", id],
    queryFn: () => fetchVisitProfileCircles(id, setState),
    enabled: false,
  });
};

export const deleteCirclePost = async (id: string) => {
  try {
    await API.graphql({
      query: deleteCircleEventPost,
      variables: {
        id,
        type: "CIRCLE",
      },
    });
    return;
  } catch (error) {
    console.error("Error deleting circle post:", error);
    return null;
  }
};

export {
  useListCircles,
  useListCircleUsers,
  useGetCircle,
  userJoinCircle,
  useGetCircleMembers,
  useGetCircleByMember,
  createMyCircle,
  userLeaveCircle,
  deleteMyCircle,
  useGetCircleInvited,
  useGetCircleJoined,
  inviteUser,
  updateInvitedCircle,
  updateMyCircle,
  useUpdateMembershipStatus,
  fetchCircle,
  fetchCGPercentContext,
  useGetCGPercentContext,
  useGetCGPercentContextWait,
  prefetchListCircle,
  useGetCircleInviteeUser,
};
